/**
 * Auth Callback Page
 *
 * Handles the OAuth callback redirect from the server.
 * Checks if the user is authenticated, merges device cards, then redirects.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { mergeDeviceToGoogleUser } from '@/lib/auth-client'

export function AuthCallback() {
  const { refreshUser } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState(false)

  useEffect(() => {
    async function handleCallback() {
      try {
        await refreshUser()
        // Merge device-based cards into Google account BEFORE navigating,
        // so the home page loads with the correct card data.
        await mergeDeviceToGoogleUser()
        navigate('/', { replace: true })
      } catch {
        setError(true)
      }
    }
    handleCallback()
  }, [refreshUser, navigate])

  // Show error after timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      setError(true)
    }, 10000)
    return () => clearTimeout(timer)
  }, [])

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface px-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-error-container mb-4">
            <span className="material-symbols-outlined text-3xl text-on-error-container">error</span>
          </div>
          <h2 className="font-headline font-bold text-xl text-on-surface mb-2">
            Sign in failed
          </h2>
          <p className="text-on-surface-variant mb-6">
            Something went wrong during authentication.
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-3 rounded-full bg-primary text-on-primary font-bold"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-container mb-4 animate-pulse">
          <span className="material-symbols-outlined text-3xl text-on-primary-container">sync</span>
        </div>
        <p className="text-on-surface-variant font-medium">Signing you in...</p>
      </div>
    </div>
  )
}
